import { useEffect, useState } from "react";
import { api, formatINR, packageLabel } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { Search, Wallet, Loader2, ArrowLeft } from "lucide-react";
import RecordPaymentDialog from "../components/RecordPaymentDialog";

export default function RecordPaymentPage() {
  const [serviceType, setServiceType] = useState("internet");
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // customer
  const [pendingBills, setPendingBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [payBill, setPayBill] = useState(null);

  // Search customers within the selected service type
  useEffect(() => {
    if (selected) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { service_type: serviceType };
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/customers", { params });
        setCustomers(data.slice(0, 25));
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, serviceType, selected]);

  // When user switches service tab, clear search results immediately
  const switchService = (v) => {
    setServiceType(v);
    setQ("");
    setCustomers([]);
    setSelected(null);
  };

  const openCustomer = async (c) => {
    setSelected(c);
    setBillsLoading(true);
    try {
      const { data } = await api.get("/bills", { params: { customer_id: c.id, status: "pending" } });
      setPendingBills(data);
    } finally {
      setBillsLoading(false);
    }
  };

  const refreshBills = async () => {
    if (!selected) return;
    const { data } = await api.get("/bills", { params: { customer_id: selected.id, status: "pending" } });
    setPendingBills(data);
    // also refresh the customer (for balance_due)
    const { data: c } = await api.get(`/customers/${selected.id}`);
    setSelected(c);
  };

  const back = () => {
    setSelected(null);
    setPendingBills([]);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Record Payment</h1>
        <p className="text-gray-500 mt-1 text-sm">Search a customer, pick a pending bill, record the payment.</p>
      </div>

      {!selected && (
        <>
          <Tabs value={serviceType} onValueChange={switchService}>
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger value="internet" data-testid="record-pay-tab-internet">Internet</TabsTrigger>
              <TabsTrigger value="cable" data-testid="record-pay-tab-cable">Cable TV</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card className="p-4">
            <div className="relative max-w-lg">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                data-testid="record-pay-search"
                autoFocus
                className="pl-9"
                placeholder={`Search ${serviceType === "internet" ? "Internet" : "Cable TV"} by name, phone, or ${serviceType === "internet" ? "user id" : "membership name"}…`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{serviceType === "internet" ? "User ID" : "Membership"}</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
                  )}
                  {!loading && customers.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">{q ? "No matches." : "No customers yet."}</TableCell></TableRow>
                  )}
                  {customers.map((c) => (
                    <TableRow key={c.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{c.customer_code}</TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell>{packageLabel(c.service_type, c.package)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.balance_due > 0 ? (
                          <span className="status-chip status-pending">{formatINR(c.balance_due)}</span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          data-testid={`select-customer-${c.id}`}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => openCustomer(c)}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {selected && (
        <>
          <Button variant="ghost" size="sm" data-testid="record-pay-back" onClick={back} className="text-gray-600">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to search
          </Button>
          <Card className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">{selected.service_type === "internet" ? "Internet · User ID" : "Cable TV · Membership"} · {selected.customer_code}</div>
                <h2 className="text-2xl font-display font-bold mt-1">{selected.name}</h2>
                <div className="text-sm text-gray-600 mt-1">{selected.phone} · {packageLabel(selected.service_type, selected.package)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-gray-500">Outstanding</div>
                <div className={`pds-stat-num text-2xl mt-1 ${selected.balance_due > 0 ? "text-red-700" : "text-gray-700"}`}>{formatINR(selected.balance_due)}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-display font-semibold text-lg mb-3">Pending bills</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billsLoading && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
                  )}
                  {!billsLoading && pendingBills.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">No pending bills. Create one from the Billing page first.</TableCell></TableRow>
                  )}
                  {pendingBills.map((b) => {
                    const remaining = b.amount - (b.paid_amount || 0);
                    return (
                      <TableRow key={b.id}>
                        <TableCell>{b.period_months} mo</TableCell>
                        <TableCell className="text-right tabular-nums">{formatINR(b.amount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatINR(b.paid_amount || 0)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatINR(remaining)}</TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-[220px] truncate" title={b.note}>{b.note || "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            data-testid={`record-payment-${b.id}`}
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => setPayBill(b)}
                          >
                            <Wallet className="h-3 w-3 mr-1" /> Record
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>

          <RecordPaymentDialog
            bill={payBill}
            open={!!payBill}
            onOpenChange={(v) => { if (!v) setPayBill(null); }}
            onDone={() => { setPayBill(null); refreshBills(); }}
          />
        </>
      )}
    </div>
  );
}
